// Builds a print-ready GST/tax invoice (Bookfleet branded) and opens it in a new
// window for the browser's native "Save as PDF" / print. Matches the backend
// invoice shape from GET /admin/reports/gst/invoice/:bookingId.

export interface GstInvoice {
  invoice_number: string;
  invoice_date: string;
  is_tax_invoice: boolean;
  seller: { name: string | null; gstin: string | null; address: string | null; state: string | null };
  buyer: { name: string | null; phone: string | null; gstin: string | null };
  booking_id: string;
  payment_id: string;
  description: string;
  charges: {
    delivery_charge: number | null;
    platform_fee: number | null;
    taxable_value?: number | null;
    gst_percent: number | null;
    cgst_amount: number | null;
    sgst_amount: number | null;
    gst_amount: number | null;
  };
  total: number | null;
  payment_method: string | null;
  payment_status: string | null;
  note?: string;
}

// ── Brand / contact constants (Bookfleet) ─────────────────────────────────────
const BRAND = 'bookfleet';
const TAGLINE = 'MOVE ANYTHING ANYTIME';
const SUPPORT_EMAIL = 'support@bookfleet.com';
const SUPPORT_PHONE = '+91 78734 56789';
const WEBSITE = 'www.bookfleet.com';
const BLUE = '#1d4ed8';

const inr = (n: number | null | undefined) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const esc = (s: unknown) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

// GST state code = first 2 chars of the seller GSTIN (e.g. 21 = Odisha).
function placeOfSupply(inv: GstInvoice): string {
  const state = inv.seller.state || '';
  const code = (inv.seller.gstin || '').trim().slice(0, 2);
  if (state && /^\d{2}$/.test(code)) return `${state} (${code})`;
  return state || '—';
}

export function invoiceHtml(inv: GstInvoice): string {
  const c = inv.charges;
  const title = inv.is_tax_invoice ? 'TAX INVOICE' : 'INVOICE';
  const half = (Number(c.gst_percent) || 0) / 2;

  // Header info rows (right column inside the blue band)
  const infoRow = (label: string, value: string) => `
    <tr>
      <td style="padding:5px 0;white-space:nowrap;color:#dbeafe;font-size:13px">${esc(label)}</td>
      <td style="padding:5px 8px;color:#dbeafe;font-size:13px">:</td>
      <td style="padding:5px 0;color:#fff;font-weight:600;font-size:13px">${value}</td>
    </tr>`;

  // Order-details rows
  const odRow = (label: string, value: string) => `
    <tr>
      <td style="padding:6px 0;color:#475569;font-size:13px;white-space:nowrap">${esc(label)}</td>
      <td style="padding:6px 8px;color:#475569;font-size:13px">:</td>
      <td style="padding:6px 0;color:#0f172a;font-weight:600;font-size:13px">${value}</td>
    </tr>`;

  // Line-item rows
  const item = (label: string, val: string) => `
    <tr>
      <td style="padding:11px 20px;border-bottom:1px dashed #e2e8f0;color:#0f172a;font-size:13px">${esc(label)}</td>
      <td style="padding:11px 20px;border-bottom:1px dashed #e2e8f0;text-align:right;color:#0f172a;font-size:13px">${val}</td>
    </tr>`;

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(inv.invoice_number)}</title>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>
  *{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#0f172a;margin:0;padding:24px;background:#eef2f7}
  .wrap{max-width:760px;margin:0 auto;background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 10px 40px rgba(2,6,23,.08)}
  .head{background:${BLUE};color:#fff;padding:28px 32px 56px}
  .head-top{display:flex;justify-content:space-between;align-items:flex-start}
  .brand{font-size:34px;font-weight:800;letter-spacing:-1px;line-height:1}
  .tagline{font-size:10px;letter-spacing:3px;color:#bfdbfe;margin-top:6px;font-weight:600}
  .doc{font-size:24px;font-weight:800;letter-spacing:.5px}
  .head-cols{display:flex;justify-content:space-between;gap:24px;margin-top:22px}
  .seller-name{font-weight:700;font-size:15px;margin-bottom:8px}
  .seller p{margin:3px 0;color:#dbeafe;font-size:13px;line-height:1.5}
  .card{background:#fff;margin:-32px 18px 0;border-radius:14px;box-shadow:0 6px 24px rgba(2,6,23,.06);padding:22px;position:relative}
  .two{display:flex;gap:20px}
  .col{flex:1}
  .col-h{color:${BLUE};font-weight:800;font-size:13px;letter-spacing:.5px;text-transform:uppercase;margin-bottom:12px}
  .buyer-name{font-weight:700;font-size:17px;margin:0 0 4px}
  .buyer p{margin:2px 0;font-size:13px;color:#334155}
  .b2c{color:${BLUE};font-weight:600;font-size:13px;margin-top:4px}
  table{width:100%;border-collapse:collapse}
  .items{margin:22px 0 0;border:1px solid #e8edf3;border-radius:12px;overflow:hidden}
  .items-head td{background:${BLUE};color:#fff;font-weight:800;font-size:12px;letter-spacing:.5px;padding:13px 20px}
  .desc td{padding:14px 20px;border-bottom:1px dashed #e2e8f0;color:#334155;font-size:13px;line-height:1.5}
  .total td{background:#eef2ff;color:${BLUE};font-weight:800;padding:16px 20px;font-size:15px}
  .total td.amt{text-align:right;font-size:20px}
  .btn-wrap{text-align:center;margin:24px 0 8px}
  .btn{background:${BLUE};color:#fff;border:0;padding:14px 30px;border-radius:12px;font-size:15px;font-weight:700;cursor:pointer;box-shadow:0 6px 16px rgba(29,78,216,.35)}
  .foot{display:flex;justify-content:space-between;gap:18px;padding:22px 26px 28px;border-top:1px solid #eef2f7}
  .foot .fcol{flex:1;font-size:12px;color:#64748b;line-height:1.5}
  .foot .ft{color:${BLUE};font-weight:700;font-size:13px;margin-bottom:3px}
  .note{margin:16px 2px 0;font-size:12px;color:#b45309;background:#fffbeb;border:1px solid #fde68a;padding:10px 14px;border-radius:10px}
  @media print{body{padding:0;background:#fff}.wrap{box-shadow:none;border-radius:0}.noprint{display:none}}
</style></head>
<body><div class="wrap">

  <!-- ── Blue header ── -->
  <div class="head">
    <div class="head-top">
      <div>
        <div class="brand">${BRAND}</div>
        <div class="tagline">${TAGLINE}</div>
      </div>
      <div class="doc">${title}</div>
    </div>
    <div class="head-cols">
      <div class="seller">
        <div class="seller-name">${esc(inv.seller.name || 'Zipto Hyperlogistics Private Limited')}</div>
        ${inv.seller.address ? `<p>${esc(inv.seller.address)}</p>` : ''}
        ${inv.seller.state ? `<p>State: ${esc(inv.seller.state)}</p>` : ''}
        ${inv.seller.gstin ? `<p>GSTIN: ${esc(inv.seller.gstin)}</p>` : ''}
      </div>
      <div>
        <table>
          ${infoRow('Invoice No.', esc(inv.invoice_number))}
          ${infoRow('Invoice Date', esc(fmtDate(inv.invoice_date)))}
          ${infoRow('Place of Supply', esc(placeOfSupply(inv)))}
          ${infoRow('Invoice Type', 'Original')}
        </table>
      </div>
    </div>
  </div>

  <!-- ── White card ── -->
  <div class="card">
    <div class="two">
      <div class="col">
        <div class="col-h">Billed To</div>
        <p class="buyer-name">${esc(inv.buyer.name || 'Customer')}</p>
        ${inv.buyer.phone ? `<p>${esc(inv.buyer.phone)}</p>` : ''}
        ${inv.buyer.gstin
          ? `<p class="b2c">GSTIN: ${esc(inv.buyer.gstin)}</p>`
          : `<p class="b2c">B2C (no GSTIN)</p>`}
      </div>
      <div class="col">
        <div class="col-h">Order Details</div>
        <table>
          ${odRow('Booking ID', esc(inv.invoice_number.replace(/^BFL-/i, '')))}
          ${odRow('Booking Date', esc(fmtDate(inv.invoice_date)))}
          ${odRow('Payment Method', esc((inv.payment_method || '—').toUpperCase()))}
          ${odRow('Payment Status', esc(((inv.payment_status || '').charAt(0).toUpperCase() + (inv.payment_status || '').slice(1)) || '—'))}
        </table>
      </div>
    </div>

    <table class="items">
      <tr class="items-head"><td>DESCRIPTION</td><td style="text-align:right">AMOUNT</td></tr>
      <tr class="desc"><td colspan="2">${esc(inv.description)}</td></tr>
      ${item('Delivery charge', inr(c.delivery_charge))}
      ${(Number(c.platform_fee) > 0) ? item('Platform fee', inr(c.platform_fee)) : ''}
      ${item('Taxable value', inr(c.taxable_value ?? ((Number(c.delivery_charge) || 0) + (Number(c.platform_fee) || 0))))}
      ${(Number(c.cgst_amount) > 0) ? item(`CGST (${half}%)`, inr(c.cgst_amount)) : ''}
      ${(Number(c.sgst_amount) > 0) ? item(`SGST (${half}%)`, inr(c.sgst_amount)) : ''}
      ${(Number(c.gst_amount) > 0 && !(Number(c.cgst_amount) > 0)) ? item(`GST (${Number(c.gst_percent) || 0}%)`, inr(c.gst_amount)) : ''}
      <tr class="total"><td>TOTAL PAID</td><td class="amt">${inr(inv.total)}</td></tr>
    </table>

    ${inv.note ? `<div class="note">${esc(inv.note)}</div>` : ''}

    <div class="btn-wrap noprint">
      <button class="btn" onclick="window.print()">⤓  Download / Print PDF</button>
    </div>
  </div>

  <!-- ── Footer ── -->
  <div class="foot">
    <div class="fcol">
      <div class="ft">Need Help?</div>
      ${esc(SUPPORT_EMAIL)}<br/>${esc(SUPPORT_PHONE)}
    </div>
    <div class="fcol" style="text-align:center">
      <div class="ft">${esc(WEBSITE)}</div>
      Thank you for choosing Bookfleet.<br/>We appreciate your trust.
    </div>
    <div class="fcol" style="text-align:right">
      <div class="ft">100% Safe &amp; Reliable</div>
      Your deliveries are<br/>in safe hands.
    </div>
  </div>

</div></body></html>`;
}

export function openGstInvoice(inv: GstInvoice) {
  const w = window.open('', '_blank', 'width=860,height=960');
  if (!w) {
    alert('Please allow pop-ups to view the invoice.');
    return;
  }
  w.document.write(invoiceHtml(inv));
  w.document.close();
  w.focus();
}
